import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const PortalAtTrigger = ({
	trigger,
	children,
	align = "center",
}: {
	trigger: HTMLElement | null;
	children: ReactNode;
	align?: "center" | "right";
}) => {
	const [rect, setRect] = useState<DOMRect | null>(null);
	const portalRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!trigger) return;
		const measure = () => setRect(trigger.getBoundingClientRect());
		measure();
		let frame = 0;
		const update = () => {
			if (frame) return;
			frame = requestAnimationFrame(() => {
				frame = 0;
				measure();
			});
		};
		const opts: AddEventListenerOptions = { passive: true, capture: true };
		window.addEventListener("scroll", update, opts);
		window.addEventListener("resize", update);
		return () => {
			if (frame) cancelAnimationFrame(frame);
			window.removeEventListener("scroll", update, opts);
			window.removeEventListener("resize", update);
		};
	}, [trigger]);

	useEffect(() => {
		const handle = (e: KeyboardEvent) => {
			if (e.key !== "Tab") return;
			const root = portalRef.current;
			if (!root) return;
			const focusables = root.querySelectorAll<HTMLElement>(
				"input, button, [tabindex]:not([tabindex='-1'])",
			);
			if (focusables.length === 0) return;
			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			const active = document.activeElement;
			if (e.shiftKey) {
				if (active === first || !root.contains(active)) {
					e.preventDefault();
					last.focus();
				}
			} else {
				if (active === last) {
					e.preventDefault();
					first.focus();
				}
			}
		};
		document.addEventListener("keydown", handle);
		return () => document.removeEventListener("keydown", handle);
	}, []);

	if (!rect) return null;

	const popoverWidth = 260;
	const triggerCenter = rect.left + rect.width / 2;
	const halfWidth = popoverWidth / 2;
	const minLeft = 8;
	const maxLeft = Math.max(8, window.innerWidth - popoverWidth - 8);
	const clampedLeft = Math.max(
		minLeft,
		Math.min(maxLeft, triggerCenter - halfWidth),
	);
	const rightPos =
		align === "right" ? Math.max(8, window.innerWidth - rect.right) : undefined;

	const popoverEstimatedHeight = 320;
	const spaceBelow = window.innerHeight - rect.bottom - 8;
	const flipAbove =
		spaceBelow < popoverEstimatedHeight && rect.top > popoverEstimatedHeight;
	const top = flipAbove ? undefined : rect.bottom + 4;
	const bottom = flipAbove
		? Math.max(8, window.innerHeight - rect.top + 4)
		: undefined;

	return createPortal(
		<div
			ref={portalRef}
			className="table-cell-portal-menu"
			style={{
				position: "fixed",
				top,
				bottom,
				left: align === "right" ? undefined : clampedLeft,
				right: rightPos,
				maxWidth: "calc(100vw - 16px)",
				maxHeight: "calc(100vh - 16px)",
				zIndex: 10000,
			}}
		>
			{children}
		</div>,
		document.body,
	);
};
